import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  useCalendarEvents,
  useCreateCalendarEvent,
  useDeleteCalendarEvent,
} from '../api/queries/calendar-events';
import { useProjects } from '../api/queries/projects';
import {
  useSyncAccounts,
  useSyncProviders,
  useToggleSyncAccount,
  useDisconnectSyncAccount,
  useStartCalendarOAuth,
  type SyncProvider,
} from '../api/queries/calendar-sync';
import { ApiError } from '../api/client';
import { apiErrorText } from '../utils/apiErrors';
import { fmtDateTime, startOfDay } from '../utils/date';
import { MONTH_GRID_CELLS, calendarRange, startOfMonthGrid } from '../utils/calendarRange';
import { DateInput } from '../components/DateInput';
import { ProjectOptions } from '../components/ProjectOptions';
import './CalendarPage.css';
import { Toggle } from '../components/Toggle';

// Combine a local-midnight epoch (from DateInput) with an HH:mm string to
// produce an epoch ms at the requested local time.
function combineDateAndTime(dateEpoch: number, timeStr: string): number {
  const [h, mn] = timeStr.split(':').map(Number);
  const d = new Date(dateEpoch);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), h ?? 0, mn ?? 0, 0, 0).getTime();
}

const RANGE_OPTIONS = [
  { label: 'This week', days: 7 },
  { label: 'This month', days: 30 },
  { label: 'Next 90 days', days: 90 },
  { label: 'Past 30 days', days: -30 },
] as const;

type ViewMode = 'list' | 'month';

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

export function CalendarPage(): JSX.Element {
  const [view, setView] = useState<ViewMode>('list');
  const [monthAnchor, setMonthAnchor] = useState(() => new Date());
  const [rangeDays, setRangeDays] = useState<number>(30);
  const [scope, setScope] = useState<'all' | 'me'>('all');
  const projectsQuery = useProjects();
  const monthGridStart = startOfMonthGrid(monthAnchor);

  // These two numbers go into the TanStack query key, and a query is identified
  // by the hash of its key VALUES. calendarRange() quantizes them to whole
  // local days for that reason: reading the clock straight into `from`/`to`
  // produced a different key on every render, so it was never the same query
  // twice — permanently pending, refetching forever, "Loading events…" that
  // never cleared. Do not reintroduce a bare Date.now() here; useMemo would
  // not save it, because identity is not what gets compared.
  const filters = calendarRange(view, rangeDays, monthAnchor, Date.now());
  const { data, isPending, error } = useCalendarEvents({
    ...filters,
    ...(scope === 'me' ? { owner_user_id: 'me' as const } : {}),
  });
  const createEvent = useCreateCalendarEvent();
  const deleteEvent = useDeleteCalendarEvent();

  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newProject, setNewProject] = useState<number | ''>('');
  const [newDate, setNewDate] = useState<number | null>(() => startOfDay(Date.now()));
  const [newAllDay, setNewAllDay] = useState(false);
  const [newStartTime, setNewStartTime] = useState('09:00');
  const [newEndTime, setNewEndTime] = useState('10:00');
  const [newLocation, setNewLocation] = useState('');
  const [newAttendees, setNewAttendees] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    if (newTitle.trim().length === 0) return;
    if (newDate === null) {
      setCreateError('Pick a date.');
      return;
    }
    const startsAt = newAllDay
      ? combineDateAndTime(newDate, '00:00')
      : combineDateAndTime(newDate, newStartTime);
    const endsAt = newAllDay
      ? combineDateAndTime(newDate, '23:59')
      : combineDateAndTime(newDate, newEndTime);
    if (endsAt < startsAt) {
      setCreateError('End time must be after start time.');
      return;
    }
    try {
      await createEvent.mutateAsync({
        title: newTitle.trim(),
        description: newDescription.trim() || undefined,
        project_id: newProject === '' ? undefined : newProject,
        starts_at: startsAt,
        ends_at: endsAt,
        all_day: newAllDay,
        location: newLocation.trim() || undefined,
        attendees: newAttendees
          .split(',')
          .map((a) => a.trim())
          .filter((a) => a.length > 0 && /\S+@\S+\.\S+/.test(a)),
      });
      setShowNew(false);
      setNewTitle('');
      setNewDescription('');
      setNewProject('');
      setNewLocation('');
      setNewAttendees('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setCreateError('Validation failed — check attendee emails.');
        return;
      }
      setCreateError('Could not save event. Try again.');
    }
  };

  if (isPending) return <p className="cal-status">Loading events…</p>;
  if (error) {
    const msg = error instanceof ApiError && error.status === 403
      ? "You don't have permission to view the calendar."
      : 'Failed to load.';
    return <p className="cal-status cal-error">{msg}</p>;
  }

  const events = data?.events ?? [];
  const projects = projectsQuery.data?.projects ?? [];

  // Group events by day for a tidy date-grouped list. JS dates here
  // use local timezone; if you need UTC consistency for shared
  // calendars, swap to ISO date keys.
  const groups: Map<string, typeof events> = new Map();
  for (const ev of events) {
    const d = new Date(ev.starts_at);
    const key = d.toLocaleDateString(undefined, {
      weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
    });
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(ev);
  }

  return (
    <section className="page-stack page-stack-wide">
      <div className="card cal-toolbar">
      <header className="cal-header">
        <h2>Calendar</h2>
        <div className="cal-header-controls">
          <div className="cal-view-toggle" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={view === 'list'}
              className={`cal-view-btn ${view === 'list' ? 'active' : ''}`}
              onClick={() => setView('list')}
            >
              List
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'month'}
              className={`cal-view-btn ${view === 'month' ? 'active' : ''}`}
              onClick={() => setView('month')}
            >
              Month
            </button>
          </div>
          {view === 'month' ? (
            <div className="cal-month-nav">
              <button
                type="button"
                className="btn-charcoal-secondary cal-month-step"
                onClick={() => setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() - 1, 1))}
              >
                ←
              </button>
              <span className="cal-month-label">
                {monthAnchor.toLocaleDateString(undefined, { year: 'numeric', month: 'long' })}
              </span>
              <button
                type="button"
                className="btn-charcoal-secondary cal-month-step"
                onClick={() => setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 1))}
              >
                →
              </button>
              <button
                type="button"
                className="cal-link"
                onClick={() => setMonthAnchor(new Date())}
              >
                today
              </button>
            </div>
          ) : (
          <select
            className="form-input cal-filter"
            value={rangeDays}
            onChange={(e) => setRangeDays(Number(e.target.value))}
          >
            {RANGE_OPTIONS.map((o) => (
              <option key={o.label} value={o.days}>{o.label}</option>
            ))}
          </select>
          )}
          <select
            className="form-input cal-filter"
            value={scope}
            onChange={(e) => setScope(e.target.value as 'all' | 'me')}
          >
            <option value="all">Everyone</option>
            <option value="me">My events</option>
          </select>
          {!showNew && (
            <button
              type="button"
              className="btn-cyan-primary"
              onClick={() => setShowNew(true)}
            >
              New event
            </button>
          )}
        </div>
      </header>
      </div>

      {showNew && (
        <div className="card">
        <form className="cal-form" onSubmit={onCreate}>
          <label className="cal-field cal-field-wide">
            <span>Title *</span>
            <input
              type="text"
              className="form-input"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              required
              maxLength={500}
              disabled={createEvent.isPending}
            />
          </label>
          <div className="cal-form-row">
            <label className="cal-field">
              <span>Date *</span>
              <DateInput
                value={newDate}
                onChange={setNewDate}
                required
                disabled={createEvent.isPending}
              />
            </label>
            <Toggle
              checked={newAllDay}
              onChange={setNewAllDay}
              label="All day"
              disabled={createEvent.isPending}
            />
            {!newAllDay && (
              <>
                <label className="cal-field">
                  <span>Start *</span>
                  <input
                    type="time"
                    className="form-input"
                    value={newStartTime}
                    onChange={(e) => setNewStartTime(e.target.value)}
                    required
                    disabled={createEvent.isPending}
                  />
                </label>
                <label className="cal-field">
                  <span>End *</span>
                  <input
                    type="time"
                    className="form-input"
                    value={newEndTime}
                    onChange={(e) => setNewEndTime(e.target.value)}
                    required
                    disabled={createEvent.isPending}
                  />
                </label>
              </>
            )}
          </div>
          <div className="cal-form-row">
            <label className="cal-field">
              <span>Project</span>
              <select
                className="form-input"
                value={newProject}
                onChange={(e) => {
                  const v = e.target.value;
                  setNewProject(v === '' ? '' : Number(v));
                }}
                disabled={createEvent.isPending}
              >
                <option value="">No project</option>
                <ProjectOptions projects={projects} />
              </select>
            </label>
            <label className="cal-field">
              <span>Location</span>
              <input
                type="text"
                className="form-input"
                value={newLocation}
                onChange={(e) => setNewLocation(e.target.value)}
                placeholder="Court room, video link, address…"
                maxLength={500}
                disabled={createEvent.isPending}
              />
            </label>
          </div>
          <label className="cal-field cal-field-wide">
            <span>Attendees (comma-separated emails)</span>
            <input
              type="text"
              className="form-input"
              value={newAttendees}
              onChange={(e) => setNewAttendees(e.target.value)}
              placeholder="counsel@firm.com, expert@example.com"
              disabled={createEvent.isPending}
            />
          </label>
          <label className="cal-field cal-field-wide">
            <span>Notes</span>
            <textarea
              className="form-input cal-textarea"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              rows={2}
              maxLength={50_000}
              disabled={createEvent.isPending}
            />
          </label>
          {createError && <p className="cal-error-banner" role="alert">{createError}</p>}
          <div className="cal-form-actions">
            <button
              type="button"
              className="btn-charcoal-secondary"
              onClick={() => {
                setShowNew(false);
                setCreateError(null);
              }}
              disabled={createEvent.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-cyan-primary"
              disabled={createEvent.isPending || newTitle.trim().length === 0}
            >
              {createEvent.isPending ? 'Saving…' : 'Save event'}
            </button>
          </div>
        </form>
        </div>
      )}

      {view === 'month' ? (
        <div className="card card-tight">
        <div className="cal-month">
          <div className="cal-month-header-row">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
              <div key={d} className="cal-month-dow">{d}</div>
            ))}
          </div>
          <div className="cal-month-grid">
            {Array.from({ length: MONTH_GRID_CELLS }).map((_, idx) => {
              const cellDate = new Date(monthGridStart);
              cellDate.setDate(monthGridStart.getDate() + idx);
              const cellStart = new Date(cellDate);
              cellStart.setHours(0, 0, 0, 0);
              const cellEnd = new Date(cellDate);
              cellEnd.setHours(23, 59, 59, 999);
              const isCurrentMonth = cellDate.getMonth() === monthAnchor.getMonth();
              const isToday = isSameDay(cellDate, new Date());
              const cellEvents = events.filter((ev) => {
                // An event lives in this cell if its [starts_at, ends_at]
                // range overlaps the cell day.
                return ev.starts_at <= cellEnd.getTime()
                    && ev.ends_at >= cellStart.getTime();
              });
              return (
                <div
                  key={idx}
                  className={`cal-month-cell ${isCurrentMonth ? '' : 'cal-month-cell-other'} ${isToday ? 'cal-month-cell-today' : ''}`}
                >
                  <div className="cal-month-cell-date">{cellDate.getDate()}</div>
                  {cellEvents.slice(0, 3).map((ev) => (
                    <div
                      key={ev.id}
                      className="cal-month-event"
                      style={{ background: ev.color ?? 'rgba(0, 245, 255, 0.18)' }}
                      title={ev.title}
                    >
                      {!ev.all_day && (
                        <span className="cal-month-event-time">
                          {new Date(ev.starts_at).toLocaleTimeString(undefined, {
                            hour: 'numeric', minute: '2-digit',
                          })}{' '}
                        </span>
                      )}
                      {ev.title}
                    </div>
                  ))}
                  {cellEvents.length > 3 && (
                    <div className="cal-month-overflow">+{cellEvents.length - 3} more</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        </div>
      ) : events.length === 0 ? (
        <div className="card">
          <div className="cal-empty">
            <p>No events in this range.</p>
          </div>
        </div>
      ) : (
        <div className="card card-tight cal-groups-card">
        <div className="cal-groups">
          {Array.from(groups.entries()).map(([day, dayEvents]) => (
            <div key={day} className="cal-group">
              <h3 className="cal-group-day">{day}</h3>
              <ul className="cal-event-list">
                {dayEvents.map((ev) => (
                  <li key={ev.id} className="cal-event">
                    <div
                      className="cal-event-color"
                      style={{ background: ev.color ?? 'var(--color-electric-cyan)' }}
                    />
                    <div className="cal-event-main">
                      <div className="cal-event-title">{ev.title}</div>
                      <div className="cal-event-meta">
                        <span>
                          {ev.all_day ? 'All day' : `${fmtDateTime(ev.starts_at)} → ${fmtDateTime(ev.ends_at)}`}
                        </span>
                        {ev.location && <span>· {ev.location}</span>}
                        {ev.project_case_number && (
                          <Link to={`/projects/${ev.project_id}`} className="cal-event-project">
                            <code>{ev.project_case_number}</code>
                          </Link>
                        )}
                      </div>
                      {ev.attendees.length > 0 && (
                        <div className="cal-event-attendees">
                          {ev.attendees.map((a) => (
                            <span key={a} className="cal-attendee-chip">{a}</span>
                          ))}
                        </div>
                      )}
                      {ev.description && (
                        <p className="cal-event-description">{ev.description}</p>
                      )}
                    </div>
                    <div className="cal-event-actions">
                      <span className="cal-event-owner">{ev.owner_name}</span>
                      <button
                        type="button"
                        className="cal-event-delete"
                        onClick={() => {
                          if (confirm(`Delete "${ev.title}"?`)) deleteEvent.mutate(ev.id);
                        }}
                        disabled={deleteEvent.isPending}
                        title="Delete event"
                        aria-label="Delete event"
                      >
                        ×
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        </div>
      )}

      <CalendarSyncCard />
    </section>
  );
}

function CalendarSyncCard(): JSX.Element {
  const accountsQ = useSyncAccounts();
  const providersQ = useSyncProviders();
  const toggle = useToggleSyncAccount();
  const disconnect = useDisconnectSyncAccount();
  const startOAuth = useStartCalendarOAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [connectError, setConnectError] = useState<string | null>(null);

  const accounts = accountsQ.data?.accounts ?? [];
  const providers = providersQ.data?.providers ?? [];

  // The OAuth callback is a browser redirect, so the query string is its only
  // way to report how it went. Both outcomes used to be dropped on the floor —
  // a denied consent bounced back to a page that said nothing at all.
  const connected = searchParams.get('oauth_connected') === '1';
  const callbackError = searchParams.get('oauth_error');
  const dismissCallbackBanner = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('oauth_connected');
    next.delete('oauth_error');
    setSearchParams(next, { replace: true });
  };

  const onConnect = async (provider: SyncProvider['name']) => {
    setConnectError(null);
    try {
      const { url } = await startOAuth.mutateAsync(provider);
      // Leaving the SPA deliberately — consent happens on the provider.
      window.location.href = url;
    } catch (err) {
      setConnectError(apiErrorText(err, 'Could not start the connection.'));
    }
  };

  return (
    <div className="card cal-sync-card">
      <h3>Calendar Sync</h3>
      <p className="cal-sync-meta">
        Connect your own Google Calendar or Microsoft 365 account and its events
        are pulled in every 10 minutes. Sync is one-way for now — events you
        create here stay here.
      </p>
      <p className="cal-sync-meta">
        Only you can see or manage this connection. The events it pulls in are a
        different matter: they land on the shared calendar, where everyone sees
        them.
      </p>

      {connected && (
        <p className="cal-sync-banner-ok" role="status">
          Account connected. Events appear after the next sync, within 10 minutes.
          <button type="button" className="cal-link" onClick={dismissCallbackBanner}>
            dismiss
          </button>
        </p>
      )}
      {callbackError !== null && (
        <p className="cal-error-banner" role="alert">
          {callbackError === 'access_denied'
            ? 'Connection cancelled — consent was not granted.'
            : `Connection failed: ${callbackError}`}
          <button type="button" className="cal-link" onClick={dismissCallbackBanner}>
            dismiss
          </button>
        </p>
      )}
      {connectError !== null && (
        <p className="cal-error-banner" role="alert">{connectError}</p>
      )}

      <div className="cal-sync-providers">
        {providers.map((p) => (
          <div key={p.name} className={`cal-sync-provider ${p.configured ? '' : 'cal-sync-provider-disabled'}`}>
            <div>
              <div className="cal-sync-provider-name">{p.label}</div>
              <div className="cal-sync-provider-status">
                {p.configured
                  ? 'Ready to connect'
                  : 'Not configured — operator must set OAuth credentials'}
              </div>
            </div>
            {p.configured && (
              <button
                type="button"
                className="btn-cyan-primary"
                onClick={() => { void onConnect(p.name); }}
                disabled={startOAuth.isPending}
              >
                {startOAuth.isPending ? 'Starting…' : 'Connect'}
              </button>
            )}
          </div>
        ))}
      </div>

      <h4 className="cal-sync-subhead">Linked accounts</h4>
      {accountsQ.isPending ? (
        <p className="cal-status">Loading…</p>
      ) : accounts.length === 0 ? (
        <p className="cal-status">No accounts linked yet.</p>
      ) : (
        <table className="cal-sync-table">
          <thead>
            <tr>
              <th>Provider</th>
              <th>Account</th>
              <th>Last sync</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.id}>
                <td>{a.provider === 'google' ? 'Google' : 'Microsoft'}</td>
                <td><code>{a.external_email}</code></td>
                <td>{a.last_sync_at ? new Date(a.last_sync_at).toLocaleString() : 'never'}</td>
                <td>{a.is_active ? 'active' : 'paused'}</td>
                <td>
                  <button
                    type="button"
                    className="cal-link"
                    onClick={() => toggle.mutate({ id: a.id, is_active: !a.is_active })}
                    disabled={toggle.isPending}
                  >
                    {a.is_active ? 'pause' : 'resume'}
                  </button>
                  <button
                    type="button"
                    className="cal-link cal-link-warn"
                    onClick={() => {
                      if (confirm(`Disconnect ${a.external_email}?`)) disconnect.mutate(a.id);
                    }}
                    disabled={disconnect.isPending}
                  >
                    disconnect
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
